import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Check, ChevronRight, Globe, Hammer } from "lucide-react";
import { CLIENT_TYPES, IMG } from "../data/content";
import { EASE, FadeUp, Overline } from "../components/Reveal";
import { CTASection } from "../components/PageHero";
import { Marquee } from "../components/Marquee";

const ABOUT_SLIDES = [
  {
    cat: "Digital Growth",
    img: "/photo-social-team.jpg",
    headline: "Grow online. Get discovered.",
    desc: "Digital marketing, social media, SEO, Meta Ads and Google Business Profile — built to bring more customers to your door.",
  },
  {
    cat: "Digital & Creative",
    img: "/photo-food-shoot.jpg",
    headline: "Look irresistible everywhere.",
    desc: "Websites, food photography and video production that build a strong brand online.",
  },
  {
    cat: "Hospitality Execution",
    img: "/photo-bottles.jpg",
    headline: "We don't just plan. We execute.",
    desc: "Events, interiors, uniforms and customized branded products — the physical side of your business, handled.",
  },
];

/* Full-screen rotating hero: one slide per category, themed background + tagline */
const AboutHero = () => {
  const slides = ABOUT_SLIDES;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % ABOUT_SLIDES.length), 4200);
    return () => clearInterval(t);
  }, [paused]);

  const s = slides[idx];
  return (
    <section
      className="relative h-[85vh] min-h-[560px] overflow-hidden bg-royal-deep"
      data-testid="about-hero-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <AnimatePresence mode="popLayout">
        <motion.img
          key={s.img}
          src={s.img}
          alt={s.cat}
          initial={{ opacity: 0, scale: 1.06, x: 70 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          exit={{ opacity: 0, x: -70 }}
          transition={{ duration: 0.9, ease: EASE }}
          className="absolute inset-0 w-full h-full object-cover"
        />
      </AnimatePresence>
      <div className="absolute inset-0 bg-royal-deep/55" aria-hidden />
      <div className="absolute inset-0 bg-gradient-to-r from-royal-deep/85 via-royal-deep/40 to-transparent" aria-hidden />

      <div className="relative max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14 h-full flex flex-col justify-center">
        <FadeUp y={12}>
          <span
            data-testid="about-hero-badge"
            className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur rounded-full px-4 py-2 text-xs font-mono uppercase tracking-[0.2em] text-saffron-warm font-semibold"
          >
            <span className="w-2 h-2 rounded-full bg-saffron animate-pulse" />
            About GrowMyRestro — 360° Hospitality Growth & Business Solutions
          </span>
        </FadeUp>
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -22 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mt-7 max-w-2xl"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-saffron-warm font-semibold" data-testid="about-slide-category">
              {s.cat}
            </p>
            <h1 className="mt-3 font-display font-extrabold tracking-tight leading-[1.08] text-4xl sm:text-5xl lg:text-6xl text-white" data-testid="about-slide-headline">
              {s.headline}
            </h1>
            <p className="mt-4 text-white/75 leading-relaxed max-w-xl">{s.desc}</p>
          </motion.div>
        </AnimatePresence>
        <FadeUp delay={0.3} className="mt-8">
          <Link
            to="/contact"
            data-testid="about-hero-cta"
            className="group inline-flex items-center gap-2 bg-saffron text-royal-deep font-bold px-7 py-3.5 rounded-full hover:bg-white transition-colors duration-300"
          >
            Let's Collaborate
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </FadeUp>
      </div>

      <button
        onClick={() => setIdx((i) => (i + 1) % slides.length)}
        data-testid="about-hero-next"
        aria-label="Next category"
        className="absolute right-5 sm:right-8 top-1/2 -translate-y-1/2 w-[52px] h-[52px] rounded-full bg-white/10 border border-white/30 backdrop-blur text-white flex items-center justify-center hover:bg-saffron hover:text-royal-deep hover:border-saffron transition-all duration-300"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <div className="absolute bottom-7 left-1/2 -translate-x-1/2 flex items-center gap-2.5">
        {slides.map((sl, i) => (
          <button
            key={sl.cat}
            onClick={() => setIdx(i)}
            data-testid={`about-hero-dot-${i}`}
            aria-label={`Show ${sl.cat}`}
            className={`h-2.5 rounded-full transition-all duration-500 ${i === idx ? "w-8 bg-saffron" : "w-2.5 bg-white/40 hover:bg-white/70"}`}
          />
        ))}
      </div>
    </section>
  );
};

const WhoWeAre = () => (
  <section className="hospitality-background py-12 sm:py-16 lg:py-20 relative overflow-hidden" data-testid="about-who-section">
    <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
      <FadeUp>
        <Overline className="justify-center">Who We Are</Overline>
        <h2 className="mt-5 font-display font-extrabold tracking-tight leading-[1.12] text-3xl sm:text-4xl text-aubergine">
          One trusted partner for <span className="text-terracotta">everything your business needs</span>
        </h2>
        <p className="mt-6 text-ink-muted leading-relaxed text-base sm:text-lg">
          GrowMyRestro helps restaurants, cafés, hotels, resorts, pubs, cloud kitchens and other hospitality
          businesses build stronger brands, reach more customers and manage their business requirements through one
          trusted partner. We bring together digital marketing, technology, creative services and practical
          hospitality solutions to help businesses grow both online and offline.
        </p>
      </FadeUp>
    </div>
  </section>
);

const WhyWeExist = () => {
  const parts = ["Marketing", "Social Media", "Google Visibility", "Advertising", "Websites", "Photography", "Videos", "Events", "Interiors", "Uniforms", "Branding"];
  return (
    <section className="hospitality-background py-12 sm:py-16 lg:py-20  grain-overlay relative overflow-hidden" data-testid="about-why-section">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center">
        <FadeUp>
          <Overline className="justify-center">Why We Exist</Overline>
          <h2 className="mt-5 font-display font-extrabold tracking-tight leading-[1.12] text-3xl sm:text-4xl text-aubergine">
            Hospitality businesses have <span className="text-saffron">many moving parts</span>
          </h2>
          <p className="mt-6 text-ink-muted leading-relaxed">
            Managing different agencies, freelancers and vendors for every requirement takes time and creates
            unnecessary complexity. GrowMyRestro was built to simplify that — we bring multiple hospitality
            solutions together under one roof.
          </p>
        </FadeUp>
        <FadeUp delay={0.15} className="mt-10 flex flex-wrap justify-center gap-2.5">
          {parts.map((p) => (
            <span key={p} className="hospitality-card bg-white border border-line rounded-full px-4 py-2 text-sm font-semibold text-aubergine shadow-sm" data-testid={`moving-part-${p.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              {p}
            </span>
          ))}
        </FadeUp>
      </div>
    </section>
  );
};

const TwoApproach = () => (
  <section className="hospitality-background py-12 sm:py-16 lg:py-20 relative overflow-hidden" data-testid="about-approach-section">
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
      <FadeUp className="text-center max-w-2xl mx-auto">
        <Overline className="justify-center">Our Two-Sided Approach</Overline>
        <h2 className="mt-5 font-display font-extrabold tracking-tight leading-[1.12] text-3xl sm:text-4xl text-aubergine">
          We grow you <span className="text-terracotta">online</span> and build you <span className="text-cardamom">offline</span>
        </h2>
      </FadeUp>
      <div className="grid lg:grid-cols-2 gap-6 mt-8 sm:mt-10">
        <FadeUp>
          <div className="h-full bg-aubergine-deep rounded-[2.5rem] p-9 sm:p-11 grain-overlay relative overflow-hidden" data-testid="approach-online-card">
            <span className="w-[52px] h-[52px] rounded-2xl bg-saffron flex items-center justify-center">
              <Globe className="w-6 h-6 text-aubergine-deep" />
            </span>
            <h3 className="mt-6 font-mono uppercase text-[11px] tracking-[0.24em] text-saffron-warm font-semibold">Online Growth</h3>
            <p className="mt-2 font-display font-bold text-2xl text-cream leading-tight">Get discovered, attract attention, generate customers</p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {["Digital Marketing", "Social Media Marketing", "SEO", "Meta Ads", "Google Business Profile", "Website Design", "Food Photography", "Video Production"].map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-sm text-cream/80">
                  <Check className="w-4 h-4 text-saffron shrink-0" /> {item}
                </li>
              ))}
            </ul>
          </div>
        </FadeUp>
        <FadeUp delay={0.12}>
          <div className="hospitality-card h-full bg-white rounded-[2.5rem] border border-line p-9 sm:p-11" data-testid="approach-offline-card">
            <span className="w-[52px] h-[52px] rounded-2xl bg-cardamom flex items-center justify-center">
              <Hammer className="w-6 h-6 text-cream" />
            </span>
            <h3 className="mt-6 font-mono uppercase text-[11px] tracking-[0.24em] text-cardamom font-semibold">Offline & Business Solutions</h3>
            <p className="mt-2 font-display font-bold text-2xl text-aubergine leading-tight">Create and manage the physical side of your business</p>
            <ul className="mt-6 space-y-3">
              {["Event Management", "Interior Design", "Hospitality Uniforms", "Customized Water Bottles", "Hospitality Branding Products"].map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-sm text-ink">
                  <Check className="w-4 h-4 text-cardamom shrink-0" /> {item}
                </li>
              ))}
            </ul>
          </div>
        </FadeUp>
      </div>
    </div>
  </section>
);

const MVPSection = () => (
  <section className="hospitality-background py-12 sm:py-16 lg:py-20  relative overflow-hidden" data-testid="about-mvp-section">
    <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
      <div className="grid lg:grid-cols-3 gap-6">
        {[
          {
            k: "Our Mission", t: "Make hospitality businesses easier to grow",
            d: "We want hospitality businesses to spend less time coordinating multiple service providers — and more time focusing on their customers and operations.",
          },
          {
            k: "Our Vision", t: "A trusted 360° partner for the hospitality industry",
            d: "A strong hospitality-focused service ecosystem where businesses access the marketing, creative, technology and practical solutions they need from one reliable partner.",
          },
          {
            k: "Our Promise", t: "We focus on your business, not just your posts",
            d: "Social media is only one part of growth. We look at the bigger picture — how customers find you, how they see your brand, how they interact with you, how they experience your business.",
          },
        ].map((c, i) => (
          <FadeUp key={c.k} delay={i * 0.1}>
            <div className="hospitality-card h-full bg-white rounded-[2rem] border border-line p-8 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(45,18,77,0.1)] transition-all duration-300" data-testid={`mvp-card-${i}`}>
              <p className="font-mono uppercase text-[10px] tracking-[0.24em] text-terracotta font-semibold">{c.k}</p>
              <h3 className="mt-3 font-display font-extrabold text-xl text-aubergine leading-snug">{c.t}</h3>
              <p className="mt-3 text-sm text-ink-muted leading-relaxed">{c.d}</p>
            </div>
          </FadeUp>
        ))}
      </div>
      <FadeUp delay={0.2} className="mt-8 sm:mt-10 text-center">
        <Link to="/contact" data-testid="about-talk-button" className="group inline-flex items-center gap-2 bg-aubergine text-cream font-bold px-7 py-3.5 rounded-full hover:bg-saffron hover:text-aubergine-deep transition-colors duration-300">
          Talk to us <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
        </Link>
      </FadeUp>
    </div>
  </section>
);

export default function About() {
  return (
    <main data-testid="about-page">
      <AboutHero />
      <div className="border-y border-line bg-white/60 py-4">
        <Marquee items={CLIENT_TYPES} className="font-mono uppercase text-xs tracking-[0.25em] text-ink-muted" fast />
      </div>
      <WhoWeAre />
      <WhyWeExist />
      <TwoApproach />
      <MVPSection />
      <CTASection
        title="Let's build and grow your hospitality business together"
        sub="Launching a restaurant, growing a café, promoting a hotel, setting up a resort, managing a cloud kitchen or improving an existing business — GrowMyRestro is ready to help."
      />
    </main>
  );
}
